import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

/**
 * Summary Details Link (Redirect to Entity Management) component.
 * Migrated from AngularJS summaryDetailsLinkCtrl used with redirect-modal.html.
 * Displays a confirmation dialog; on Yes the host (AngularJS) performs redirect
 * to entity details via the request-grid-update listener.
 */
@Component({
    selector: 'gtw-summary-details-link',
    templateUrl: './summary-details-link.component.html',
    styleUrls: ['./summary-details-link.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SummaryDetailsLinkComponent implements OnInit, OnDestroy {

  /** Emits 'refresh' when user confirms redirect; 'cancel' when user cancels. */
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  /** Prevents multiple init runs when multiple @Input setters fire. */
  private isInitialized = false;

  /** Row data from grid (LEGAL_ENTITY_ID, LEGAL_ENTITY_NAME, etc.). Passed from AngularJS resolve. */
  rowData: any = {};

  /** Active screen filter params (e.g. tax_year). Used by host for redirect logic. */
  filterParams: any = {};

  /** Global config and params from header-data. */
  globalService: any = {};
  GENERAL_CONFIG: any = {};

  /** Controls visibility of the confirmation modal. */
  showModal = false;

  /** Config for the shared confirmation modal (title, message, buttons). */
  modalConfig: ConfirmationModalConfig = {
    title: 'Redirect To Entity Management',
    message: 'Proceed to entity details management?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.showModal = true;
    this.cdr.detectChanges();
  }

  /**
   * User confirmed redirect. Emit 'refresh' so AngularJS listener runs redirect logic and closes modal.
   */
  onConfirm(): void {
    this.requestGridUpdate.emit('refresh');
  }

  /**
   * User cancelled. Emit 'cancel' so AngularJS listener closes the modal.
   */
  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
